import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import type { UnpackResponse } from "../../../api/src/lib";
import type { AdminController } from "../../../api/src/admin/admin.controller";

// The cross-firm audit payload returned by the staff-guarded
// `GET /admin/rebalances/:rebalanceId/audit` endpoint
// (s-rebalance-audit-analysis-pipeline-019eb94b D8/R11). Carries the D7
// `findings` array (for the inline cards) and the pointer `maxSeverity` (for
// the list/header rollup).
export type AdminRebalanceAudit = UnpackResponse<
  AdminController["getRebalanceAudit"]
>["data"];

type WrappedAudit = UnpackResponse<AdminController["getRebalanceAudit"]>;

export interface UseAdminRebalanceAuditResult {
  audit: AdminRebalanceAudit | undefined;
  /** True once the request has settled (success OR error). */
  settled: boolean;
}

/**
 * Fetch the cross-firm audit payload for the read-only admin viewer, so it can
 * be primed into the firm tree's own `useAudit` cache key before the tree
 * mounts (D8 — the third primed stream alongside explain + check). GET-only.
 *
 * A failure (5xx / network) resolves to `undefined` `audit` with `settled:
 * true` — the audit cards are a non-blocking augmentation, so a missing audit
 * degrades to "no findings" rather than failing the whole Explain view.
 */
export function useAdminRebalanceAudit(
  rebalanceId: number | undefined,
): UseAdminRebalanceAuditResult {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = rebalanceId != null && Number.isInteger(rebalanceId);

  const query = useQuery<AdminRebalanceAudit>({
    queryKey: ["admin", "rebalances", rebalanceId, "audit"],
    enabled,
    retry: false,
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const res = await fetch(
        `${process.env.REACT_APP_API_ROOT}/admin/rebalances/${rebalanceId}/audit`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`);
      }
      const body = (await res.json()) as WrappedAudit;
      return body.data;
    },
  });

  if (!enabled) {
    return { audit: undefined, settled: true };
  }
  return {
    audit: query.data,
    settled: query.isSuccess || query.isError,
  };
}
